import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  createHeatmapConfig,
  getLegendItemsFromConfig,
  HeatmapConfig,
  shapeToVectorLayer,
  useProjectionContext,
} from '@ais-ui/react-openlayers';
import { LegendConfig } from '@ais-ui/websockets';
import { Feature, FeatureCollection } from 'geojson';
import {
  useGetGridJobConfigGridById,
  useGetGridJobResultsById,
} from '@ais-ui/api';
import { useDateTimeFormat } from '../../conversions/useDateFormat';

interface GPSLostContextProps {
  children: ReactNode;
}

export interface GpsLostGridCell {
  id: string;
  configurationId: number;
  instanceId: number;
  mmsis: number[];
  index: number;
  gridLayerId: string;
}

export interface GridJobInstance {
  id: string | number;
  status: string;
  timerange?: {
    lower: Date;
    upper: Date;
  };
}

interface GPSLostContextValue {
  selectedGPSLostGridCell: GpsLostGridCell | null;
  setSelectedGPSLostGridCell: Dispatch<SetStateAction<GpsLostGridCell | null>>;
  clickCoordinate: number[] | null;
  setClickCoordinate: Dispatch<SetStateAction<number[] | null>>;
  selectedGPSLostInstance: any;
  setSelectedGPSLostInstance: Dispatch<SetStateAction<any>>;
  gpsLostLegend: LegendConfig<'interval'> | null;
  instances: GridJobInstance[];
  setInstances: Dispatch<SetStateAction<GridJobInstance[]>>;
  selectedInstanceIndex: number;
  setSelectedInstanceIndex: Dispatch<SetStateAction<number>>;
  createGridLayerAndSetSelectedInstance: (
    configuration: any,
    instance: GridJobInstance,
    gpsLostGridId: string
  ) => Promise<{
    layer: ReturnType<typeof shapeToVectorLayer>;
    maxGpsLostValue: number;
  }>;
  selectedGPSLostConfiguration: any;
  setSelectedGPSLostConfiguration: Dispatch<SetStateAction<any>>;
  selectedMmsis: number[];
  setSelectedMmsis: Dispatch<SetStateAction<number[]>>;
  heatmapConfig: HeatmapConfig | null;
}

interface ExtendedFeatureCollection extends FeatureCollection {
  properties?: {
    name?: string;
    timerange?: {
      lower: Date;
      upper: Date;
    };
    type?: string;
    id?: string;
    heatmapConfig?: HeatmapConfig | null;
  };
}

// Define the type for results
interface GridJobResults {
  timerange: {
    lower: Date;
    upper: Date;
  };
  grid_cells_result: Array<{
    element_id: number;
    ships_w_lost_gps: number;
    mmsi_list: Array<string | number>;
  }>;
}

const GPSLostContext = createContext<GPSLostContextValue | undefined>(
  undefined
);

export const GPSLostProvider = ({ children }: GPSLostContextProps) => {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const { displayProjection, dataProjection } = useProjectionContext();

  const [selectedGPSLostGridCell, setSelectedGPSLostGridCell] =
    useState<GpsLostGridCell | null>(null);
  const [clickCoordinate, setClickCoordinate] = useState<number[] | null>(null);

  const getHexagonGrid = useGetGridJobConfigGridById();
  const getInstanceDetails = useGetGridJobResultsById();

  const [selectedGPSLostConfiguration, setSelectedGPSLostConfiguration] =
    useState<any>();
  const [selectedGPSLostInstance, setSelectedGPSLostInstance] = useState<any>();
  const [instances, setInstances] = useState<GridJobInstance[]>([]);
  const [selectedMmsis, setSelectedMmsis] = useState<number[]>([]);
  const [heatmapConfig, setHeatmapConfig] = useState<HeatmapConfig | null>(
    null
  );

  useEffect(() => {
    const handleRemoveGPSLostGrid = (event: CustomEvent) => {
      setSelectedGPSLostInstance(null);
      setSelectedGPSLostGridCell(null);
      setSelectedMmsis([]);
      setHeatmapConfig(null);
    };

    window.addEventListener(
      'removeGPSLostGrid',
      handleRemoveGPSLostGrid as EventListener
    );

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener(
        'removeGPSLostGrid',
        handleRemoveGPSLostGrid as EventListener
      );
    };
  }, []);

  const [selectedInstanceIndex, setSelectedInstanceIndex] = useState(0);

  useEffect(() => {
    if (!instances || instances.length === 0) {
      return;
    }
    setSelectedInstanceIndex(0);
  }, [instances]);

  const gpsLostLegend: LegendConfig<'interval'> | null = useMemo(() => {
    if (!selectedGPSLostInstance) return null;
    if (!heatmapConfig) return null;

    const start = selectedGPSLostInstance.timerange.lower;
    const end = selectedGPSLostInstance.timerange.upper;

    return {
      type: 'interval',
      metric: 'ships',
      unit: 'count',
      title: 'GPS Lost',
      subtitle: `${formatDateTimeWithTimeZone(
        start,
        true
      )} to ${formatDateTimeWithTimeZone(end, true)}`,
      items: getLegendItemsFromConfig(heatmapConfig, 1), // Use alpha = 1 for legend
    };
  }, [selectedGPSLostInstance, heatmapConfig, formatDateTimeWithTimeZone]);

  const createGridLayerAndSetSelectedInstance = useCallback(
    async (
      configuration: any,
      instance: GridJobInstance,
      gpsLostGridId: string
    ) => {
      const hexagonGrid = (await getHexagonGrid(
        configuration.id
      )) as ExtendedFeatureCollection;
      const instanceDetails = (await getInstanceDetails(
        configuration.id,
        instance.id
      )) as GridJobResults;

      setSelectedGPSLostInstance(instanceDetails);
      setSelectedGPSLostConfiguration(configuration);
      setSelectedMmsis([]);
      const newHeatmapConfig = createHeatmapConfig(
        instanceDetails.grid_cells_result.map(
          (cell: any) => cell.ships_w_lost_gps
        )
      );

      setHeatmapConfig(newHeatmapConfig);

      // Add name and timerange to the main GeoJSON object
      hexagonGrid.properties = {
        name: 'hexagon',
        timerange: {
          lower: instanceDetails.timerange.lower,
          upper: instanceDetails.timerange.upper,
        },
        type: 'gps-lost',
        id: gpsLostGridId,
      };

      let maxGpsLostValue = 0;
      const gpsLostValues = [];
      // Process each feature to add necessary properties
      hexagonGrid.features.forEach((feature: Feature & { properties: any }) => {
        const featureIndex = feature.properties.index;

        // Add id property to each feature
        feature.properties.id = `hexagon_cell_${featureIndex}`;

        // Find matching grid cell in the result
        const matchingCell = instanceDetails.grid_cells_result.find(
          (cell) => cell.element_id === featureIndex
        );

        if (matchingCell) {
          // If a match is found, add the match data
          feature.properties.ships_w_lost_gps = matchingCell.ships_w_lost_gps;
          feature.properties.mmsi_list = matchingCell.mmsi_list;

          if (matchingCell.ships_w_lost_gps > maxGpsLostValue) {
            maxGpsLostValue = matchingCell.ships_w_lost_gps;
          }
          gpsLostValues.push(matchingCell.ships_w_lost_gps);
        } else {
          // If no match, add default values
          feature.properties.ships_w_lost_gps = 0;
          feature.properties.mmsi_list = [];
        }
        feature.properties.configurationId = configuration.id;
        feature.properties.instanceId = instance.id;
        feature.properties.type = 'gps-lost';
        feature.properties.gridLayerId = gpsLostGridId;
      });

      hexagonGrid.properties.heatmapConfig = newHeatmapConfig;

      const layer = shapeToVectorLayer(
        hexagonGrid as any,
        displayProjection,
        dataProjection
      );

      return { layer, maxGpsLostValue };
    },
    [getHexagonGrid, getInstanceDetails, displayProjection, dataProjection]
  );

  const value: GPSLostContextValue = {
    selectedGPSLostGridCell,
    setSelectedGPSLostGridCell,
    clickCoordinate,
    setClickCoordinate,
    selectedGPSLostInstance,
    setSelectedGPSLostInstance,
    gpsLostLegend,
    instances,
    setInstances,
    selectedInstanceIndex,
    setSelectedInstanceIndex,
    createGridLayerAndSetSelectedInstance,
    selectedGPSLostConfiguration,
    setSelectedGPSLostConfiguration,
    selectedMmsis,
    setSelectedMmsis,
    heatmapConfig,
  };

  return (
    <GPSLostContext.Provider value={value}>{children}</GPSLostContext.Provider>
  );
};

export const useGPSLost = () => {
  const context = useContext(GPSLostContext);
  if (!context) {
    throw new Error('useGPSLost must be used within a GPSLostContextProvider');
  }
  return context;
};
